import { LineItemMap, TypeMap, SizeMap } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { GetQuotes } from 'smg/customer-api/v2/quotes';
import { GetProofURLs } from 'smg/proofs';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';

import * as carts from '../carts';
import { Relref } from '../hugo';
import { AccountReady } from '../account-session';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await AccountReady) {
        return;
    }
    const PAGE_SIZE = 10;
    const DATA_FIELDS = 'serial,created,quoteLineItems,quoteProofs,cachedTotal,key';
    const $searchForm = document.querySelector('#quote-search-form') as HTMLFormElement;
    const $searchInput = document.querySelector('#quote-search-input') as HTMLInputElement;
    const $sort = document.querySelector('#quote-sort') as HTMLSelectElement;
    const $count = document.querySelector('#quote-count') as HTMLElement;
    const $list = document.querySelector('#quote-list') as HTMLElement;
    const $skeleton = document.querySelector('#quote-skeleton') as HTMLElement;
    const $emptyNone = document.querySelector('#quote-empty-none') as HTMLElement;
    const $emptySearch = document.querySelector('#quote-empty-search') as HTMLElement;
    const $emptySearchTitle = document.querySelector('#empty-search-title') as HTMLElement;
    const $emptySearchCta = document.querySelector('#empty-search-cta') as HTMLAnchorElement;
    const $pagination = document.querySelector('#quote-pagination') as HTMLElement;

    // ── State from URL ──

    const qs = QueryString();
    let currentSearch = qs.get('q') || '';
    let currentSort = qs.get('sort') || 'newest';
    let currentPage = parseInt(qs.get('page') || '1', 10);

    $searchInput.value = currentSearch;
    $sort.value = currentSort;

    // ── Load quotes ──

    async function loadQuotes() {
        showSkeleton();

        try {
            const data = await GetQuotes({
                sort: currentSort === 'oldest' ? 'created asc' : 'created desc',
                max: PAGE_SIZE,
                p: currentPage - 1,
                data: DATA_FIELDS,
                q: currentSearch || undefined,
            });
            const quotes = data.results;

            if (quotes.length === 0 && currentPage === 1) {
                if (currentSearch) {
                    showEmpty('search');
                } else {
                    showEmpty('none');
                }
                return;
            }

            $list.innerHTML = '';
            const f = document.createDocumentFragment();
            for (const quote of quotes) {
                f.appendChild(await renderQuoteCard(quote));
            }
            $list.appendChild(f);

            if (quotes.length < PAGE_SIZE && currentPage === 1) {
                $count.textContent = `${quotes.length} quote${quotes.length === 1 ? '' : 's'}`;
            } else {
                $count.textContent = '';
            }

            renderPagination(data.nextPage);
            showContent();
        } catch {
            showContent();
            $list.innerHTML = '<div class="text-center text-muted py-4">Something went wrong loading quotes.</div>';
        }
    }

    // ── Render quote card ──

    async function renderQuoteCard(quote: models.Quote): Promise<HTMLElement> {
        const el = template.Clone('#quote-card-template').firstElementChild as HTMLElement;

        // Header
        const $num = el.querySelector('.quote-card-num') as HTMLElement;
        $num.textContent = `#${quote.serial ?? ''}`;

        const $date = el.querySelector('.quote-card-date') as HTMLElement;
        if (quote.created?.isValid) {
            $date.textContent = quote.created.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' });
        }

        // Line items
        const $items = el.querySelector('.quote-card-items') as HTMLElement;
        if (quote.quoteLineItems?.length) {
            $items.appendChild(await renderCompactLineItems(quote.quoteLineItems));
        }

        // Proofs
        const $proofs = el.querySelector('.quote-card-proofs') as HTMLElement;
        if (quote.quoteProofs?.length) {
            $proofs.classList.remove('d-none');
            const fancyboxGroup = `quote-${quote.id ?? ''}`;
            for (const qp of quote.quoteProofs) {
                const proof = qp.proof;
                if (!proof) {
                    continue;
                }
                const urls = GetProofURLs(window.cloudFront, proof.file || '');
                const $link = document.createElement('a');
                $link.href = urls.main;
                $link.setAttribute('data-fancybox', fancyboxGroup);
                const $img = document.createElement('img');
                $img.className = 'quote-card-proof';
                $img.src = urls.thumb || urls.main;
                $img.alt = qp.name || `Proof ${qp.number ?? ''}`;
                $img.loading = 'lazy';
                $link.appendChild($img);
                $proofs.appendChild($link);
            }
        }

        // Footer — total
        const $total = el.querySelector('.quote-card-total') as HTMLElement;
        $total.textContent = DollarFormat(quote.cachedTotal || 0);

        // Actions
        const $actions = el.querySelector('.quote-card-actions') as HTMLElement;
        const quoteKey = quote.key ?? '';

        // Add to cart
        const $addToCart = document.createElement('button');
        $addToCart.className = 'btn btn-primary btn-sm';
        $addToCart.type = 'button';
        $addToCart.innerHTML = '<i class="fa fa-shopping-cart"></i> Add to cart';
        carts.CartToggle({
            $btn: $addToCart,
            type: 'quote',
            id: quote.id ?? '',
            key: quoteKey,
            addLabel: 'Add to cart',
        });

        // View quote link
        const $view = document.createElement('a');
        $view.className = 'btn btn-outline-primary btn-sm';
        $view.href = `${Relref('/view')}?id=${encodeURIComponent(quoteKey)}`;
        $view.textContent = 'View quote';

        $actions.appendChild($view);
        $actions.appendChild($addToCart);

        return el;
    }

    // ── Compact line items ──

    async function renderCompactLineItems(items: models.QuoteLineItem[]): Promise<HTMLElement> {
        const $container = document.createElement('div');

        for (const item of items) {
            const itemParts = [
                (await LineItemMap()).get(item.lineItemID || '')?.networkNamePlural() || item.lineItem,
                (await TypeMap()).get(item.lineItemTypeID || '')?.networkName() || item.type,
                (await SizeMap()).get(item.lineItemTypeSizeID || '')?.networkName() || item.size,
                item.description,
            ].filter(Boolean);

            const $parent = document.createElement('div');
            $parent.className = 'li-parent';
            $parent.innerHTML = `<span class="li-name">${escHtml(itemParts.join(' \u00b7 '))}</span><span class="li-qty">\u00d7${(item.quantity || 0).toLocaleString()}</span>`;
            $container.appendChild($parent);

            for (const child of item.quoteChildLineItems || []) {
                const childParts = [child.child, child.option, child.description].filter(Boolean);
                if (!childParts.length) {
                    continue;
                }
                const $child = document.createElement('div');
                $child.className = 'li-child';
                if (child.quantity && child.quantity !== item.quantity) {
                    $child.innerHTML = `<span class="li-name">${escHtml(childParts.join(' \u00b7 '))}</span><span class="li-qty">\u00d7${child.quantity.toLocaleString()}</span>`;
                } else {
                    $child.innerHTML = `<span class="li-name">${escHtml(childParts.join(' \u00b7 '))}</span>`;
                }
                $container.appendChild($child);
            }
        }

        return $container;
    }

    // ── Pagination ──

    function renderPagination(hasNextPage: boolean) {
        $pagination.innerHTML = '';
        if (currentPage === 1 && !hasNextPage) {
            $pagination.classList.add('d-none');
            return;
        }
        $pagination.classList.remove('d-none');

        const $prev = document.createElement('button');
        $prev.className = 'account-pag-btn';
        $prev.innerHTML = '<i class="material-icons" style="font-size:14px">chevron_left</i>';
        $prev.disabled = currentPage <= 1;
        $prev.addEventListener('click', () => goToPage(currentPage - 1));
        $pagination.appendChild($prev);

        const $btn = document.createElement('button');
        $btn.className = 'account-pag-btn active';
        $btn.textContent = String(currentPage);
        $pagination.appendChild($btn);

        const $next = document.createElement('button');
        $next.className = 'account-pag-btn';
        $next.innerHTML = '<i class="material-icons" style="font-size:14px">chevron_right</i>';
        $next.disabled = !hasNextPage;
        $next.addEventListener('click', () => goToPage(currentPage + 1));
        $pagination.appendChild($next);
    }

    function goToPage(page: number) {
        currentPage = page;
        syncURL();
        window.scrollTo({ top: 0 });
        void loadQuotes();
    }

    // ── UI state helpers ──

    let skeletonTimer: ReturnType<typeof setTimeout> | undefined;

    function showSkeleton() {
        clearTimeout(skeletonTimer);
        $emptyNone.classList.add('d-none');
        $emptySearch.classList.add('d-none');
        $pagination.classList.add('d-none');
        $count.textContent = '';
        skeletonTimer = setTimeout(() => {
            $list.innerHTML = '';
            $skeleton.classList.remove('d-none');
        }, 500);
    }

    function showContent() {
        clearTimeout(skeletonTimer);
        $skeleton.classList.add('d-none');
        $emptyNone.classList.add('d-none');
        $emptySearch.classList.add('d-none');
    }

    function showEmpty(type: 'none' | 'search') {
        clearTimeout(skeletonTimer);
        $skeleton.classList.add('d-none');
        $list.innerHTML = '';
        $pagination.classList.add('d-none');
        $count.textContent = '';
        $emptyNone.classList.toggle('d-none', type !== 'none');
        $emptySearch.classList.toggle('d-none', type !== 'search');

        if (type === 'search') {
            $emptySearchTitle.textContent = `No results for \u201c${currentSearch}\u201d`;
        }
    }

    function syncURL() {
        const params = new URLSearchParams();
        if (currentSearch) {
            params.set('q', currentSearch);
        }
        if (currentSort !== 'newest') {
            params.set('sort', currentSort);
        }
        if (currentPage > 1) {
            params.set('page', String(currentPage));
        }
        const url = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        history.replaceState(null, '', url);
    }

    // ── Event handlers ──

    $searchForm.addEventListener('submit', e => {
        e.preventDefault();
        currentSearch = $searchInput.value.trim();
        currentPage = 1;
        syncURL();
        void loadQuotes();
    });

    $sort.addEventListener('change', () => {
        currentSort = $sort.value;
        currentPage = 1;
        syncURL();
        void loadQuotes();
    });

    $emptySearchCta.addEventListener('click', e => {
        e.preventDefault();
        currentSearch = '';
        currentPage = 1;
        $searchInput.value = '';
        syncURL();
        void loadQuotes();
    });

    // ── Initial load ──
    await loadQuotes();
}

function escHtml(s: string): string {
    const el = document.createElement('span');
    el.textContent = s;
    return el.innerHTML;
}
